/**
 * 💳 Cash Out with Stripe — artist-facing self-serve panel.
 * -----------------------------------------------------------
 * Renders inside the Payouts page under Rail 1 (Stripe Connect) and lets
 * eligible artists submit a Stripe Cash Out request. This NEVER initiates
 * a Stripe Transfer directly — it only creates a `payout_requests` row
 * with method="stripe" + status="pending". The admin remains the sole
 * executor via POST /admin/payouts/{id}/send-via-stripe.
 *
 * Preconditions enforced client-side (backend re-validates authoritatively):
 *   • Stripe Connect status === "ready" AND payouts_enabled === true
 *   • Verified spendable_available_usd ≥ $25
 *   • No existing pending/approved Stripe cash-out for this user
 *
 * Props:
 *   • summary       — from GET /payouts/me/summary
 *   • history       — items[] from GET /payouts/me/history
 *   • stripeStatus  — from GET /stripe/connect/status (via StripeConnectCard)
 *   • onSubmitted() — called after a successful request so the parent refreshes
 */
import { useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api from "@/lib/api";

const fmtUSD = (n) => `$${Number(n || 0).toFixed(2)}`;

const STATUS_BADGE = {
  pending: "bg-amber-500/20 text-amber-200 border-amber-400/40",
  approved: "bg-blue-500/20 text-blue-200 border-blue-400/40",
  denied: "bg-rose-500/20 text-rose-200 border-rose-400/40",
  paid: "bg-emerald-500/20 text-emerald-200 border-emerald-400/40",
};

export default function StripeCashOutPanel({ summary, history, stripeStatus, onSubmitted }) {
  const min = Number(summary?.min_payout_usd || 25);
  const available = Number(summary?.spendable_available_usd || 0);
  const connectReady =
    stripeStatus?.stripe_connect_status === "ready" &&
    stripeStatus?.stripe_connect_payouts_enabled === true;

  // Detect an already-open Stripe request (pending or approved). The
  // backend enforces this too — displaying it here keeps the UX honest
  // and the button inert when a duplicate would be rejected.
  const openStripeRequest = useMemo(
    () =>
      (history || []).find(
        (p) =>
          p.method === "stripe" && (p.status === "pending" || p.status === "approved")
      ),
    [history]
  );

  const [amount, setAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submittedRequest, setSubmittedRequest] = useState(null);

  // ─── NOT connected / onboarding / restricted ──────────────────
  if (!stripeStatus || !connectReady) {
    return (
      <Card
        className="p-4 mb-4 bg-blue-500/5 border border-blue-400/30"
        data-testid="stripe-cashout-panel-not-ready"
      >
        <p className="text-sm font-bold text-blue-100 mb-1">💳 Cash Out with Stripe</p>
        <p className="text-xs text-blue-100/70">
          Finish Stripe Connect onboarding above to unlock one-click cash out.
          Your verified earnings and the $25 minimum still apply.
        </p>
      </Card>
    );
  }

  // ─── Open request already pending — show truthful pending state ──
  if (openStripeRequest && !submittedRequest) {
    return (
      <Card
        className="p-4 mb-4 bg-amber-500/10 border border-amber-400/40"
        data-testid="stripe-cashout-panel-pending-existing"
      >
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <p className="text-sm font-bold text-amber-100">
              💳 Cash Out Request Submitted
            </p>
            <p className="text-xs text-amber-100/80 mt-1">
              Your Stripe cash out for{" "}
              <span className="font-bold">{fmtUSD(openStripeRequest.amount_usd)}</span> is
              awaiting RGM review before the Stripe transfer is initiated. You'll be
              notified once it's processed — <strong>funds have not reached your bank yet</strong>.
            </p>
            <p className="text-[11px] text-amber-100/60 mt-1 font-mono">
              {openStripeRequest.request_id}
            </p>
          </div>
          <span
            className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border ${
              STATUS_BADGE[openStripeRequest.status] || STATUS_BADGE.pending
            }`}
            data-testid="stripe-cashout-pending-status"
          >
            {openStripeRequest.status}
          </span>
        </div>
      </Card>
    );
  }

  const submit = async () => {
    if (submitting) return;
    const amt = Math.round(parseFloat(amount) * 100) / 100;
    if (!isFinite(amt) || amt < min) {
      toast.error(`Minimum cash out is ${fmtUSD(min)}`);
      return;
    }
    if (amt > available) {
      toast.error(`You can cash out up to ${fmtUSD(available)}`);
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/payouts/me/request", {
        method: "stripe",
        amount_usd: amt,
        legal_name: "", // backend auto-fills from user profile for stripe
        artist_name: "",
        handle: "", // backend fills with connected account id
        notes: "",
      });
      setSubmittedRequest(r.data.request);
      toast.success("Cash Out Request Submitted — pending RGM review");
      setAmount("");
      await onSubmitted?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      let msg = "Couldn't submit Stripe cash out";
      if (typeof d === "string") msg = d;
      else if (d && typeof d === "object" && d.message) msg = d.message;
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Freshly submitted confirmation (session-scoped) ──
  if (submittedRequest) {
    return (
      <Card
        className="p-4 mb-4 bg-emerald-500/10 border border-emerald-400/40"
        data-testid="stripe-cashout-panel-submitted"
      >
        <p className="text-sm font-bold text-emerald-100">
          ✅ Cash Out Request Submitted
        </p>
        <p className="text-xs text-emerald-100/80 mt-1">
          Your Stripe cash out for{" "}
          <span className="font-bold">{fmtUSD(submittedRequest.amount_usd)}</span> is
          now pending RGM review. Once approved, RGM initiates the Stripe transfer to
          your connected account — funds typically arrive in your bank in 1–3 business
          days thereafter. <strong>Funds have not been transferred yet.</strong>
        </p>
        <p className="text-[11px] text-emerald-100/60 mt-1 font-mono">
          {submittedRequest.request_id}
        </p>
      </Card>
    );
  }

  // ─── Eligible — show the cash out form ──
  const eligible = available >= min;
  return (
    <Card
      className="p-4 mb-4 bg-gradient-to-br from-emerald-500/10 to-blue-500/10 border border-emerald-400/40"
      data-testid="stripe-cashout-panel"
    >
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <p className="text-sm font-bold text-emerald-100">💳 Cash Out with Stripe</p>
        <span
          className="text-[10px] uppercase tracking-wider font-bold bg-emerald-500/20 border border-emerald-400/40 text-emerald-100 px-2 py-0.5 rounded-full"
          data-testid="stripe-cashout-ready-chip"
        >
          Stripe Connect ✓ Ready
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
        <div className="rounded-md bg-black/30 border border-emerald-400/20 px-3 py-2">
          <p className="text-[10px] uppercase tracking-wider text-emerald-200/80 font-bold">
            Available to Cash Out
          </p>
          <p
            className="text-lg font-black text-emerald-100 mt-0.5"
            data-testid="stripe-cashout-available"
          >
            {fmtUSD(available)}
          </p>
          <p className="text-[10px] text-emerald-100/60">verified paid-backed</p>
        </div>
        <div className="rounded-md bg-black/30 border border-emerald-400/20 px-3 py-2">
          <p className="text-[10px] uppercase tracking-wider text-emerald-200/80 font-bold">
            Minimum
          </p>
          <p className="text-lg font-black text-emerald-100 mt-0.5">{fmtUSD(min)}</p>
          <p className="text-[10px] text-emerald-100/60">per RGM policy</p>
        </div>
        <div className="rounded-md bg-black/30 border border-emerald-400/20 px-3 py-2">
          <p className="text-[10px] uppercase tracking-wider text-emerald-200/80 font-bold">
            Requested Amount (USD)
          </p>
          <Input
            type="number"
            step="0.01"
            min={min}
            max={available}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder={eligible ? available.toFixed(2) : min.toFixed(2)}
            disabled={!eligible || submitting}
            data-testid="stripe-cashout-amount-input"
            className="bg-black/40 border-emerald-400/40 text-white mt-1 h-8 text-sm"
          />
          {eligible && (
            <button
              type="button"
              onClick={() => setAmount(available.toFixed(2))}
              data-testid="stripe-cashout-max-btn"
              className="text-[10px] text-emerald-200 hover:text-emerald-100 underline mt-1"
            >
              Use max: {fmtUSD(available)}
            </button>
          )}
        </div>
      </div>
      <p
        className="text-[11px] text-emerald-100/80 mb-3 leading-relaxed"
        data-testid="stripe-cashout-disclosure"
      >
        🔒 Your request is submitted to RGM for review before any Stripe transfer is
        initiated. Pressing this button <strong>does not</strong> move funds — RGM
        verifies your eligible paid-backed earnings, then an admin triggers the Stripe
        transfer. Funds typically settle in your linked bank 1–3 business days after
        the transfer is initiated.
      </p>
      <Button
        type="button"
        onClick={submit}
        disabled={!eligible || submitting || !amount}
        className="w-full bg-gradient-to-r from-emerald-500 to-blue-500 hover:from-emerald-400 hover:to-blue-400 text-black font-black disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="payouts-stripe-cashout-btn"
      >
        {submitting
          ? "Submitting…"
          : !eligible
          ? `Earn ${fmtUSD(min)} to cash out`
          : "💳 Cash Out with Stripe"}
      </Button>
    </Card>
  );
}
